<template>
    <div class="customer-service-agent">

        <!-- Chat Messages Container -->
        <div class="chat-messages" ref="chatMessages">
            <!-- Rate Limit Indicator (at top if showing metadata) -->
            <rate-limit-indicator
                v-if="showMetadata"
                :remaining="rateLimitRemaining"
                :show="showMetadata"
                class="mb-3"
            />

            <!-- Clear History Button (at top, only show if there are responses) -->
            <div v-if="responses.length > 0" class="clear-history-container top-position">
                <button
                    type="button"
                    class="clear-history-btn"
                    @click="clearHistory"
                    title="Clear chat history"
                >
                    <i class="fas fa-trash-alt"></i>
                    <span>Clear History</span>
                </button>
            </div>

            <!-- Welcome Message -->
            <div v-if="responses.length === 0 && !isLoading" class="welcome-message">
                <div class="welcome-content">
                    <div class="welcome-logo">
                        <!-- Custom logo or fallback to icon -->
                        <img 
                            v-if="logoUrl" 
                            :src="logoUrl" 
                            :alt="logoAlt"
                            class="logo-image"
                            @error="handleLogoError"
                        />
                        <div v-else class="logo-placeholder" :style="{ background: logoGradient }">
                            <i class="fas fa-building"></i>
                        </div>
                    </div>
                    <div class="welcome-text">
                        <p class="mb-0">{{ welcomeMessage }}</p>
                    </div>
                </div>
            </div>

            <!-- Chat Conversation -->
            <div v-for="response in responses" :key="response.id" class="conversation-pair">
                <!-- User Question -->
                <div class="user-message">
                    <div class="message-content">
                        <div class="message-bubble user-bubble">
                            {{ response.question }}
                        </div>
                    </div>
                </div>

                <!-- Bot Response or Loading -->
                <div class="bot-message">
                    <div class="message-content full-width">
                        <!-- Show loading if response is pending -->
                        <div v-if="response.isPending" class="typing-indicator-simple">
                            <div class="typing-dots">
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="dot"></span>
                            </div>
                        </div>
                        <!-- Show actual response when available -->
                        <response-display
                            v-else
                            :response="response"
                            :show-metadata="showMetadata"
                            :is-latest="isLatestResponse(response)"
                            :typewriter-effect="shouldShowTypewriter(response)"
                            :full-width="true"
                            @copy="copyToClipboard"
                            class="chat-response full-width-response"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Fixed Input Area at Bottom -->
        <div class="chat-input-container">
            <!-- Error Display above input -->
            <error-message
                v-if="errorMessage"
                :error="errorMessage"
                :error-type="errorType"
                @retry="retryLastQuestion"
                @clear="clearError"
                class="mb-2"
            />

            <!-- Question Input Form -->
            <question-input
                v-model="currentQuestion"
                :is-loading="isLoading"
                :character-limit="characterLimit"
                :error="inputError"
                @submit="submitQuestion"
                @input="clearInputError"
                placeholder="Type your question here..."
            />
        </div>
    </div>
</template>

<script>
import QuestionInput from './components/QuestionInput.vue'
import ResponseDisplay from './components/ResponseDisplay.vue'
import ErrorMessage from './components/ErrorMessage.vue'
import RateLimitIndicator from './components/RateLimitIndicator.vue'
import aiFaqApi from '../mixins/aiFaqApi.js'

export default {
    name: 'CustomerServiceAgent',

    components: {
        QuestionInput,
        ResponseDisplay,
        ErrorMessage,
        RateLimitIndicator
    },

    mixins: [aiFaqApi],

    props: {
        occasionId: {
            type: [Number, String],
            required: true
        },
        apiEndpoint: {
            type: String,
            default: '/api/v0/ai/ask-question'
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        maxQuestions: {
            type: Number,
            default: 5
        },
        characterLimit: {
            type: Number,
            default: 500
        },
        participantId: {
            type: [Number, String],
            default: null
        },
        historyExpiryHours: {
            type: Number,
            default: 24 // 24 hours by default
        },
        welcomeMessage: {
            type: String,
            default: "Hello! I'm your customer service assistant. How can I help you today?"
        },
        logoUrl: {
            type: String,
            default: null // Will use default icon if not provided
        },
        logoAlt: {
            type: String,
            default: 'Organization Logo'
        },
        organizationName: {
            type: String,
            default: 'Organization'
        },
        brandColor: {
            type: String,
            default: '#007bff' // Default blue
        }
    },

    data() {
        return {
            currentQuestion: '',
            responses: [],
            isLoading: false,
            errorMessage: '',
            errorType: null,
            inputError: '',
            rateLimitRemaining: 10,
            lastQuestion: '',
            typedResponseIds: new Set() // Track which responses have been typed
        }
    },

    computed: {
        reversedResponses() {
            return [...this.responses].reverse()
        },
        
        logoGradient() {
            // Create a gradient from brandColor to a slightly darker version
            const darkColor = this.darkenColor(this.brandColor, 20)
            return `linear-gradient(135deg, ${this.brandColor}, ${darkColor})`
        }
    },

    mounted() {
        // Load chat history when component mounts
        this.loadChatHistory()
    },

    beforeDestroy() {
        // Save chat history when component is about to be destroyed
        this.saveChatHistory()
    },

    watch: {
        // Save chat history whenever responses change
        responses: {
            handler() {
                this.saveChatHistory()
            },
            deep: true
        },

        // Save when rate limit changes
        rateLimitRemaining() {
            this.saveChatHistory()
        }
    },

    methods: {
        async submitQuestion() {
            // Validate input
            if (!this.validateQuestion()) {
                return
            }
            this.clearError()
            this.clearInputError()
            this.lastQuestion = this.currentQuestion.trim()

            // Create a pending response immediately to show the question
            const pendingResponse = {
                id: Date.now(),
                question: this.lastQuestion,
                isPending: true,
                timestamp: new Date()
            }

            // Add to responses and clear current question
            this.responses.push(pendingResponse)
            this.currentQuestion = ''

            // Scroll to bottom to show the new question
            this.$nextTick(() => {
                this.scrollToBottom()
            })

            // Emit event
            this.$emit('question-submitted', {
                question: this.lastQuestion,
                occasionId: this.occasionId,
                participantId: this.participantId,
            })

            try {
                const response = await this.askQuestion(this.lastQuestion, this.occasionId, this.participantId)
                this.handleSuccessResponse(response, pendingResponse)
            } catch (error) {
                this.handleErrorResponse(error, pendingResponse)
            }
        },

        validateQuestion() {
            const question = this.currentQuestion.trim()

            if (!question) {
                this.inputError = 'Please enter a question'
                return false
            }

            if (question.length < 5) {
                this.inputError = 'Question must be at least 10 characters long'
                return false
            }

            if (question.length > this.characterLimit) {
                this.inputError = `Question must be no more than ${this.characterLimit} characters long`
                return false
            }

            // Basic HTML/script detection
            if (/<[^>]*>/.test(question)) {
                this.inputError = 'HTML tags are not allowed'
                return false
            }

            return true
        },

        handleSuccessResponse(apiResponse, pendingResponse) {
            // Find the pending response and update it
            const responseIndex = this.responses.findIndex(r => r.id === pendingResponse.id)
            if (responseIndex !== -1) {
                // Update the existing pending response with the API response
                const updatedResponse = {
                    ...pendingResponse,
                    answer: apiResponse.answer,
                    sources: apiResponse.sources || [],
                    cached: apiResponse.cached || false,
                    tokensUsed: apiResponse.tokens_used,
                    model: apiResponse.model,
                    rateLimitRemaining: apiResponse.rate_limit_remaining,
                    isPending: false // Remove pending state
                }

                // Replace the pending response with the complete response
                this.$set(this.responses, responseIndex, updatedResponse)

                // Mark this response as one that should show typewriter effect
                this.typedResponseIds.add(updatedResponse.id)

                // Update rate limit
                if (typeof apiResponse.rate_limit_remaining === 'number') {
                    this.rateLimitRemaining = apiResponse.rate_limit_remaining
                }

                // Limit history if needed
                if (this.responses.length > this.maxQuestions) {
                    this.responses = this.responses.slice(-this.maxQuestions)
                }

                // Scroll to bottom of chat
                this.$nextTick(() => {
                    this.scrollToBottom()
                })

                // Also scroll during typing animation
                setTimeout(() => {
                    this.scrollToBottom()
                }, 100)

                // Emit event
                this.$emit('response-received', updatedResponse)
            }
        },

        handleErrorResponse(error, pendingResponse) {
            console.error('AI FAQ Error:', error)

            // Remove the pending response since there was an error
            if (pendingResponse) {
                const responseIndex = this.responses.findIndex(r => r.id === pendingResponse.id)
                if (responseIndex !== -1) {
                    this.responses.splice(responseIndex, 1)
                }
            }

            let errorMessage = 'An unexpected error occurred. Please try again.'
            let errorType = 'general'

            if (error.response) {
                const status = error.response.status
                const data = error.response.data

                if (status === 429) {
                    errorType = 'rate-limit'
                    errorMessage = 'Too many requests. Please wait a moment before asking another question.'
                    this.rateLimitRemaining = 0
                    this.$emit('rate-limit-exceeded', { error: data })
                } else if (status === 400 && data.error) {
                    errorType = 'validation'
                    errorMessage = data.error
                } else if (status === 404) {
                    errorType = 'not-found'
                    errorMessage = 'Event not found. Please check the event details.'
                } else if (status >= 500) {
                    errorType = 'server'
                    errorMessage = 'Server error. Our team has been notified. Please try again later.'
                }
            } else if (error.code === 'NETWORK_ERROR') {
                errorType = 'network'
                errorMessage = 'Network error. Please check your connection and try again.'
            }

            this.errorMessage = errorMessage
            this.errorType = errorType

            this.$emit('error-occurred', {
                error: error,
                message: errorMessage,
                type: errorType
            })
        },

        retryLastQuestion() {
            if (this.lastQuestion) {
                this.currentQuestion = this.lastQuestion
                this.clearError()
                this.submitQuestion()
            }
        },

        clearError() {
            this.errorMessage = ''
            this.errorType = null
        },

        clearInputError() {
            this.inputError = ''
        },

        clearHistory() {
            this.clearChatHistory() // This will clear both memory and localStorage
            this.$emit('history-cleared')
        },

        copyToClipboard(text) {
            // Try modern clipboard API first
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$emit('text-copied', text)
                }).catch(err => {
                    console.error('Clipboard API failed:', err)
                    this.fallbackCopy(text)
                })
            } else {
                this.fallbackCopy(text)
            }
        },

        fallbackCopy(text) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            textArea.style.top = '-999999px'
            document.body.appendChild(textArea)
            textArea.focus()
            textArea.select()

            try {
                const successful = document.execCommand('copy')
                if (successful) {
                    this.$emit('text-copied', text)
                } else {
                    console.error('Fallback copy failed')
                }
            } catch (err) {
                console.error('Fallback copy error:', err)
            } finally {
                document.body.removeChild(textArea)
            }
        },

        scrollToBottom() {
            const chatMessages = this.$refs.chatMessages
            if (chatMessages) {
                chatMessages.scrollTop = chatMessages.scrollHeight
                // Force smooth scrolling behavior
                chatMessages.scrollTo({
                    top: chatMessages.scrollHeight,
                    behavior: 'smooth'
                })
            }
        },

        isLatestResponse(response) {
            return this.responses.length > 0 && this.responses[this.responses.length - 1].id === response.id
        },

        shouldShowTypewriter(response) {
            // Only show typewriter effect if:
            // 1. It's the latest response AND
            // 2. It hasn't been typed before (not loaded from localStorage)
            return this.isLatestResponse(response) && !this.typedResponseIds.has(response.id)
        },

        // Chat history persistence methods
        getStorageKey() {
            return `chat_history_${this.occasionId}_${this.participantId || 'guest'}`
        },

        saveChatHistory() {
            try {
                const chatData = {
                    responses: this.responses,
                    rateLimitRemaining: this.rateLimitRemaining,
                    typedResponseIds: Array.from(this.typedResponseIds), // Convert Set to Array for storage
                    timestamp: Date.now()
                }
                localStorage.setItem(this.getStorageKey(), JSON.stringify(chatData))
            } catch (error) {
                console.warn('Failed to save chat history:', error)
            }
        },

        loadChatHistory() {
            try {
                const stored = localStorage.getItem(this.getStorageKey())
                if (stored) {
                    const chatData = JSON.parse(stored)

                    // Check if the stored data is not too old (configurable hours)
                    const maxAge = this.historyExpiryHours * 60 * 60 * 1000 // Convert hours to milliseconds
                    if (Date.now() - (chatData.timestamp || 0) < maxAge) {
                        this.responses = chatData.responses || []
                        this.rateLimitRemaining = chatData.rateLimitRemaining || 10

                        // Restore typed response IDs and ensure all loaded responses are marked as typed
                        this.typedResponseIds = new Set(chatData.typedResponseIds || [])
                        // Mark ALL loaded responses as already typed to prevent typewriter effect
                        this.responses.forEach(response => {
                            if (response.id && !response.isPending) {
                                this.typedResponseIds.add(response.id)
                            }
                        })

                        // Scroll to bottom after loading history
                        this.$nextTick(() => {
                            this.scrollToBottom()
                        })
                    } else {
                        // Remove old data
                        this.clearChatHistory()
                    }
                }
            } catch (error) {
                console.warn('Failed to load chat history:', error)
            }
        },

        clearChatHistory() {
            try {
                localStorage.removeItem(this.getStorageKey())
            } catch (error) {
                console.warn('Failed to clear chat history:', error)
            }
            this.responses = []
            this.rateLimitRemaining = 10
            this.typedResponseIds.clear() // Clear the typed response tracking
        },

        // Logo and branding helper methods
        handleLogoError(event) {
            console.warn('Failed to load logo:', this.logoUrl)
            // Hide the broken image and show fallback
            event.target.style.display = 'none'
            // You could emit an event here to notify parent component
            this.$emit('logo-load-error', this.logoUrl)
        },

        darkenColor(color, percent) {
            // Convert hex to RGB, darken, and convert back
            const hex = color.replace('#', '')
            const r = parseInt(hex.substr(0, 2), 16)
            const g = parseInt(hex.substr(2, 2), 16)
            const b = parseInt(hex.substr(4, 2), 16)
            
            const darkenedR = Math.floor(r * (100 - percent) / 100)
            const darkenedG = Math.floor(g * (100 - percent) / 100)
            const darkenedB = Math.floor(b * (100 - percent) / 100)
            
            return `rgb(${darkenedR}, ${darkenedG}, ${darkenedB})`
        }
    }
}
</script>

<style scoped>
.customer-service-agent {
    display: grid;
    grid-template-rows: 1fr auto;
    height: 100%;
    max-width: 100%;
    border-radius: 8px;
    overflow: hidden;
}


/* Chat Messages Container */
.chat-messages {
    overflow-y: auto;
    scroll-behavior: smooth;
    min-height: 0;
    padding-right:3px;
}

/* Welcome Message */
.welcome-message {
    margin-bottom: 2rem;
    padding: 2rem 1rem;
}

.welcome-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 400px;
    margin: 0 auto;
}

.welcome-logo {
    margin-bottom: 1.5rem;
}

.logo-placeholder {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #007bff, #0056b3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.logo-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.welcome-text {
    color: #495057;
    font-size: 1.1rem;
    line-height: 1.6;
    font-weight: 400;
}

.welcome-text p {
    margin: 0;
}

/* Message Layout */
.bot-message, .user-message {
    display: flex;
    align-items: flex-start;
    margin-bottom: 1rem;
}

.user-message {
    justify-content: flex-end;
}

.user-message .message-content {
    max-width: 75%;
}

.bot-message .message-content.full-width {
    width: 100%;
    max-width: 100%;
}

/* Message Bubbles */
.message-bubble {
    padding: 0.75rem 1rem;
    border-radius: 18px;
    margin-bottom: 0.5rem;
    word-wrap: break-word;
}

.bot-bubble {
    background: #fff;
    border: 1px solid #e9ecef;
    border-bottom-left-radius: 6px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.user-bubble {
    background: #007bff;
    color: white;
    border-bottom-right-radius: 6px;
    margin-left: auto;
    text-align: right;
}

/* Typing Indicator */
.typing-indicator-simple {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 1rem 0;
}

.typing-dots {
    display: flex;
    gap: 4px;
}

.typing-dots .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #6c757d;
    animation: typing-dot 1.4s infinite ease-in-out;
}

.typing-dots .dot:nth-child(1) {
    animation-delay: 0s;
}

.typing-dots .dot:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-dots .dot:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing-dot {
    0%, 80%, 100% {
        transform: scale(0.8);
        opacity: 0.5;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

/* Chat Response Styling Override */
.chat-response {
    margin-bottom: 0;
}

/* Fixed Input Container */
.chat-input-container {
    background: #fff;
    padding-top:1rem;
}

/* Clear History Button */
.clear-history-container {
    display: flex;
    justify-content: center;
    margin-bottom: 0.75rem;
}

.clear-history-container.top-position {
    justify-content: center;
    margin-bottom: 0.5rem;
    padding-right: 0.5rem;
}

.clear-history-btn {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    background: transparent;
    border: none;
    color: #9ca3af;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.clear-history-btn:hover {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
}

.clear-history-btn:active {
    transform: scale(0.98);
}

.clear-history-btn i {
    font-size: 0.7rem;
}

.clear-history-btn span {
    font-weight: 400;
}

/* Conversation Pair Styling */
.conversation-pair {
    margin-bottom: 1.5rem;
}

.conversation-pair:last-child {
    margin-bottom: 0;
}

/* Scrollbar Styling */
.chat-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

/* Mobile Responsive */
@media (max-width: 768px) {
    .customer-service-agent {
        border-radius: 0;
        height: 100vh; /* Ensure full viewport height on mobile */
        max-height: 100vh;
    }

    .chat-messages {
        /* Remove max-height restriction to allow full height */
        min-height: 0; /* Allow grid to work properly */
    }

    .message-content {
        max-width: 85%;
    }

    .message-bubble {
        padding: 0.5rem 0.75rem;
        font-size: 0.9rem;
    }

    .welcome-message {
        padding: 1.5rem 0.75rem;
    }

    .logo-placeholder,
    .logo-image {
        width: 60px;
        height: 60px;
    }
    
    .logo-placeholder {
        font-size: 24px;
    }

    .welcome-text {
        font-size: 1rem;
    }

    .clear-history-btn {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
    }

    .clear-history-btn i {
        font-size: 0.7rem;
    }
}

/* Animation for new messages */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.bot-message, .user-message {
    animation: slideIn 0.3s ease-out;
}

</style>



// WEBPACK FOOTER //
// src/Assets/components/AiAgents/CustomerService/CustomerServiceAgent.vue